
import { ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import AdminSearchBar from "./AdminSearchBar";
import EnhancedNotificationDropdown from "./notifications/EnhancedNotificationDropdown";
import { 
  LayoutDashboard, Package, FolderTree, ShoppingCart, Users, BarChart3, Settings, Menu, LogOut, Bell, Grid3X3, Moon, Sun, Monitor, Layout, Crown,
  TrendingUp, Brain, FileText, Mail, Gavel, ChevronDown, ChevronRight, Megaphone, PieChart, BrainCircuit, MessageSquare
} from "lucide-react";

interface AdminLayoutProps {
  children: ReactNode;
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { signOut, isSuperAdmin, userRole } = useOptimizedAuth();
  const { theme, effectiveTheme, toggleTheme } = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    analytics: true, // Keep analytics expanded by default since user is on ML page
  });

  const navigationGroups = [
    {
      name: 'Dashboard',
      items: [
        { name: 'Dashboard', href: '/admin', icon: LayoutDashboard }
      ]
    },
    {
      name: 'Content Management',
      items: [
        { name: 'Hero Content', href: '/admin/hero-content', icon: Layout },
        { name: 'Products', href: '/admin/products', icon: Package },
        { name: 'Categories', href: '/admin/categories', icon: FolderTree },
        { name: 'Reviews', href: '/admin/reviews', icon: MessageSquare }
      ]
    },
    {
      name: 'Sales & Orders',
      items: [
        { name: 'Orders', href: '/admin/orders', icon: ShoppingCart }
      ]
    },
    {
      name: 'User Management',
      items: [
        { name: 'Users', href: '/admin/users', icon: Users },
        { name: 'Contact Messages', href: '/admin/contact-messages', icon: Mail },
        { name: 'Notifications', href: '/admin/notifications', icon: Bell },
        { name: 'Notification Analytics', href: '/admin/notifications/analytics', icon: Megaphone }
      ]
    },
    {
      name: 'Analytics & Reports',
      items: [
        { name: 'Analytics Overview', href: '/admin/analytics', icon: BarChart3 },
        { name: 'Product Analytics', href: '/admin/analytics/products', icon: PieChart },
        { name: 'ML Analytics', href: '/admin/analytics/ml', icon: Brain },
        { name: 'Advanced Reporting', href: '/admin/reporting', icon: FileText }
      ]
    },
    {
      name: 'Legal & Compliance',
      items: [
        { name: 'Legal Documents', href: '/admin/legal-documents', icon: Gavel },
        { name: 'Email Templates', href: '/admin/email-templates', icon: Mail },
        { name: 'Email Logs', href: '/admin/email-logs', icon: FileText },
        { name: 'Newsletter', href: '/admin/newsletter', icon: Megaphone }
      ]
    },
    {
      name: 'System Settings',
      items: [
        { name: 'Settings', href: '/admin/settings', icon: Settings }
      ]
    }
  ];

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const isGroupActive = (items: typeof navigationGroups[0]['items']) => {
    return items.some(item => location.pathname === item.href || location.pathname.startsWith(item.href + '/'));
  };

  const getThemeIcon = () => {
    if (theme === 'system') return <Monitor className="h-5 w-5" />;
    return effectiveTheme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />;
  };

  const NavLink = ({ item, mobile = false }: { item: typeof navigationGroups[0]['items'][0]; mobile?: boolean }) => {
    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    const Icon = item.icon;

    return (
      <Link
        to={item.href}
        onClick={() => mobile && setSidebarOpen(false)}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
          isActive
            ? 'bg-blue-600 text-white shadow-lg'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
        }`}
      >
        <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'group-hover:text-blue-600'}`} />
        {item.name}
        {isActive && <div className="w-1 h-4 bg-white rounded-full ml-auto" />}
      </Link>
    );
  };

  const NavigationGroup = ({ group, mobile = false }: { group: typeof navigationGroups[0]; mobile?: boolean }) => {
    const groupKey = group.name.toLowerCase().replace(/\s+/g, '-');
    const isExpanded = expandedGroups[groupKey];
    const hasActiveItem = isGroupActive(group.items);

    if (group.name === 'Dashboard') {
      return (
        <div className="space-y-1">
          {group.items.map(item => <NavLink key={item.name} item={item} mobile={mobile} />)}
        </div>
      );
    }

    return (
      <Collapsible 
        open={isExpanded || hasActiveItem} 
        onOpenChange={(open) => !hasActiveItem && setExpandedGroups(prev => ({ ...prev, [groupKey]: open }))}
      >
        <CollapsibleTrigger asChild>
          <Button 
            variant="ghost" 
            className={`w-full justify-start text-sm font-medium transition-all duration-200 px-3 py-2 ${
              hasActiveItem ? 'text-blue-600 bg-blue-50 dark:bg-blue-950/20' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            <span className="flex items-center gap-2 flex-1">
              <span>{group.name}</span>
            </span>
            {(isExpanded || hasActiveItem) ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1 ml-2">
          {group.items.map(item => <NavLink key={item.name} item={item} mobile={mobile} />)}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <div className="min-h-screen flex">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-grow bg-card border-r pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-6 mb-8">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 ${isSuperAdmin ? 'bg-gradient-to-br from-purple-600 to-pink-600' : 'bg-gradient-to-br from-blue-600 to-purple-600'} rounded-lg flex items-center justify-center`}>
                {isSuperAdmin ? <Crown className="h-5 w-5 text-white" /> : <Grid3X3 className="h-5 w-5 text-white" />}
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">VRAM Admin</h1>
                {isSuperAdmin && (
                  <Badge variant="destructive" className="text-xs px-2 py-0.5">
                    SUPER ADMIN
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 flex-grow flex flex-col px-3">
            <nav className="flex-1 space-y-3">
              {navigationGroups.map(group => <NavigationGroup key={group.name} group={group} />)}
            </nav>
            <div className="flex-shrink-0 flex border-t pt-4">
              <Button
                variant="ghost"
                onClick={signOut}
                className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <LogOut className="h-5 w-5 mr-3" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Header */}
        <header className="bg-card border-b px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Sidebar */}
              <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" className="lg:hidden">
                    <Menu className="h-6 w-6" />
                    <span className="sr-only">Open sidebar</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0 bg-card">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center flex-shrink-0 px-6 py-5">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 ${isSuperAdmin ? 'bg-gradient-to-br from-purple-600 to-pink-600' : 'bg-gradient-to-br from-blue-600 to-purple-600'} rounded-lg flex items-center justify-center`}>
                          {isSuperAdmin ? <Crown className="h-5 w-5 text-white" /> : <Grid3X3 className="h-5 w-5 text-white" />}
                        </div>
                        <div>
                          <h1 className="text-foreground font-bold text-xl text-left">VRAM</h1>
                          {isSuperAdmin && (
                            <Badge variant="destructive" className="text-xs px-2 py-0.5">
                              SUPER ADMIN
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow flex flex-col px-3">
                      <nav className="flex-1 space-y-3">
                        {navigationGroups.map(group => <NavigationGroup key={group.name} group={group} mobile />)}
                      </nav>
                      <div className="flex-shrink-0 flex border-t pt-4">
                        <Button
                          variant="ghost"
                          onClick={signOut}
                          className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-accent"
                        >
                          <LogOut className="h-5 w-5 mr-3" />
                          Sign out
                        </Button>
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl font-bold text-foreground hidden sm:block">
                  {navigationGroups.flatMap(group => group.items).find(item => location.pathname === item.href || location.pathname.startsWith(item.href + '/'))?.name || 'Dashboard'}
                </h1>
                {isSuperAdmin && (
                  <Badge variant="destructive" className="hidden sm:flex items-center gap-1">
                    <Crown className="h-3 w-3" />
                    Super Admin
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <AdminSearchBar />
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="text-muted-foreground hover:text-foreground"
                title={`Current: ${theme} (${effectiveTheme})`}
              >
                {getThemeIcon()}
              </Button>
              <EnhancedNotificationDropdown />
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 bg-background">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
